import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingsApi, portsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import PortSearch from '../../components/PortSearch';
import { 
  Ship, 
  Package, 
  MapPin,
  Calendar as CalendarIcon,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Check,
  User,
  Building,
  Phone,
  Mail,
  Globe
} from 'lucide-react';
import { format } from 'date-fns';
import { containerTypes, cargoTypes, formatCurrency } from '../../lib/utils';
import { ShipmentModeToggle, BulkCargoFields, BulkSummary, useBulkOptions, estimateBulkPrice, bulkPayload } from '../../components/booking/BulkFields';
import { formatHs } from '../../components/booking/HsCodeField';
import { CargoLinesEditor, newLine, linesPayload, linesTotals, validateLines } from '../../components/booking/CargoLinesEditor';
import { toast } from 'sonner';

const countries = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", "Belgium", 
  "Brazil", "Canada", "Chile", "China", "Colombia", "Denmark", "Egypt", "Finland", "France", "Germany", 
  "Greece", "Hong Kong", "India", "Indonesia", "Ireland", "Italy", "Japan", "Kenya", "Malaysia", "Mexico", 
  "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru", "Philippines", "Poland", "Portugal", 
  "Russia", "Saudi Arabia", "Singapore", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden", 
  "Switzerland", "Taiwan", "Thailand", "Turkey", "UAE", "UK", "USA", "Vietnam"
];

function ProgressStep({ number, label, active, completed }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm transition-colors ${
        completed ? 'bg-blue-500 text-white' : 
        active ? 'bg-white text-ink' : 
        'bg-white/20 text-white/60'
      }`}>
        {completed ? <Check className="h-5 w-5" /> : number}
      </div>
      <span className={`hidden sm:inline font-medium ${active ? 'text-white' : 'text-white/60'}`}>
        {label}
      </span>
    </div>
  );
}

function AgentWalkinBooking() {
  const navigate = useNavigate();
  const { user, isAgent, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ports, setPorts] = useState([]);
  const [step, setStep] = useState(1);
  const [pickupDate, setPickupDate] = useState(null);

  // Customer details for walk-in
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    country: ''
  });
  
  // Booking details
  const [formData, setFormData] = useState({
    origin_port: '',
    destination_port: '',
    shipment_mode: 'container',
    bulk_cargo_type: 'dry_bulk',
    bulk_commodity: '',
    quantity_mt: '',
    vessel_class: '',
    loading_terms: 'FIOST',
    cargo_type: 'FCL',
    container_type: '40ft',
    container_count: 1,
    pickup_date: '',
    special_requirements: '',
    cargo_lines: [newLine()]
  });

  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const bulkOptions = useBulkOptions();

  useEffect(function() {
    if (!isAgent && !isAdmin) {
      toast.error('Access denied. Agent or Admin privileges required.');
      navigate('/');
      return;
    }
    loadPorts();
  }, [isAgent, isAdmin]);

  useEffect(function() {
    calculatePrice();
  }, [formData.container_type, formData.container_count, formData.cargo_type, formData.shipment_mode, formData.bulk_commodity, formData.quantity_mt, bulkOptions]);

  async function loadPorts() {
    try {
      const response = await portsApi.getAll();
      setPorts(response.data);
    } catch (err) {
      console.error('Failed to load ports:', err);
    }
  }

  function calculatePrice() {
    if (formData.shipment_mode === 'bulk') {
      setEstimatedPrice(estimateBulkPrice(bulkOptions, formData.bulk_commodity, Number(formData.quantity_mt)));
      return;
    }
    const basePrices = { '20ft': 1500, '40ft': 2500, '40ft_hc': 2800, '20ft_reefer': 2500, '40ft_reefer': 4000 };
    const cargoMultipliers = { 'FCL': 1.0, 'LCL': 0.6 };
    const base = basePrices[formData.container_type] || 2500;
    const multiplier = cargoMultipliers[formData.cargo_type] || 1.0;
    const price = base * formData.container_count * multiplier;
    const fuelSurcharge = price * 0.15;
    const handlingFee = 150 * formData.container_count;
    setEstimatedPrice(Math.round(price + fuelSurcharge + handlingFee));
  }

  function handleCustomerChange(field, value) {
    setCustomerData(function(prev) { return { ...prev, [field]: value }; });
  }

  function handleChange(field, value) {
    setFormData(function(prev) { return { ...prev, [field]: value }; });
  }

  function handleDateSelect(date) {
    setPickupDate(date);
    handleChange('pickup_date', date ? date.toISOString() : '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Validate customer data
    if (!customerData.name || !customerData.country) {
      toast.error('Please enter customer name and country');
      return;
    }

    // Validate booking data
    if (!formData.origin_port || !formData.destination_port) {
      toast.error('Please select origin and destination ports');
      return;
    }
    const lineErr = validateLines(formData.cargo_lines, 'booking', formData.shipment_mode === 'bulk');
    if (lineErr) {
      toast.error(lineErr);
      return;
    }
    if (!formData.pickup_date) {
      toast.error('Please select pickup date');
      return;
    }
    if (formData.shipment_mode === 'bulk' && (!formData.bulk_commodity || Number(formData.quantity_mt) < 500)) {
      toast.error('Select a bulk commodity and enter at least 500 metric tons');
      return;
    }

    setLoading(true);
    try {
      const bookingPayload = {
        ...formData,
        ...bulkPayload(formData),
        cargo_lines: linesPayload(formData.cargo_lines, formData.shipment_mode === 'bulk'),
        weight: formData.shipment_mode === 'bulk' ? undefined : linesTotals(formData.cargo_lines, false).gross,
        walk_in_customer: {
          name: customerData.name,
          email: customerData.email || null,
          phone: customerData.phone || null,
          company: customerData.company || null,
          country: customerData.country
        }
      };
      
      const response = await bookingsApi.create(bookingPayload);
      toast.success('Walk-in booking created successfully!');
      
      const basePath = isAdmin ? '/admin' : '/agent';
      navigate(basePath + '/bookings', { state: { newBooking: response.data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  }

  function nextStep() {
    if (step === 1) {
      if (!customerData.name) {
        toast.error('Please enter customer name');
        return;
      }
      if (!customerData.country) {
        toast.error('Please select customer country');
        return;
      }
    }
    if (step === 2) {
      if (!formData.origin_port || !formData.destination_port) {
        toast.error('Please select origin and destination ports');
        return;
      }
      if (!formData.pickup_date) {
        toast.error('Please select a pickup date');
        return;
      }
    }
    if (step === 3) {
      const lineErr = validateLines(formData.cargo_lines, 'booking', formData.shipment_mode === 'bulk');
      if (lineErr) { toast.error(lineErr); return; }
    }
    if (step === 3 && formData.shipment_mode === 'bulk' && (!formData.bulk_commodity || Number(formData.quantity_mt) < 500)) {
      toast.error('Select a bulk commodity and enter at least 500 metric tons');
      return;
    }
    setStep(step + 1);
  }

  function prevStep() {
    setStep(step - 1);
  }

  const basePath = isAdmin ? '/admin' : '/agent';
  const isBackdated = Boolean(pickupDate) && format(pickupDate, 'yyyy-MM-dd') < format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="space-y-6" data-testid="walkin-booking">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0F1B26] to-[#0F1B26] rounded-sm p-8 text-white">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 bg-white/10 rounded-sm flex items-center justify-center">
            <User className="h-7 w-7 text-blue-600" />
          </div>
          <div>
            <h1 className="text-[32px] sv-display">Walk-in booking</h1>
            <p className="text-white/70">Create a booking for a walk-in customer</p>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-4 mt-8">
          <ProgressStep number={1} label="Customer" active={step >= 1} completed={step > 1} />
          <div className="flex-1 h-1 bg-white/20 rounded">
            <div className={`h-full bg-blue-500 rounded transition-all ${step > 1 ? 'w-full' : 'w-0'}`}></div>
          </div>
          <ProgressStep number={2} label="Route" active={step >= 2} completed={step > 2} />
          <div className="flex-1 h-1 bg-white/20 rounded">
            <div className={`h-full bg-blue-500 rounded transition-all ${step > 2 ? 'w-full' : 'w-0'}`}></div>
          </div>
          <ProgressStep number={3} label="Cargo" active={step >= 3} completed={step > 3} />
          <div className="flex-1 h-1 bg-white/20 rounded">
            <div className={`h-full bg-blue-500 rounded transition-all ${step > 3 ? 'w-full' : 'w-0'}`}></div>
          </div>
          <ProgressStep number={4} label="Confirm" active={step >= 4} completed={step > 4} />
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Step 1: Customer Details */}
        {step === 1 && (
          <div className="bg-white rounded-sm shadow-card p-8 animate-fadeInUp" data-testid="step-customer">
            <h2 className="text-2xl font-normal tracking-[-0.01em] text-ink mb-6 flex items-center gap-3">
              <User className="h-6 w-6 text-blue-600" />
              Customer Information
            </h2>
            <p className="text-gray-600 mb-6">Enter the walk-in customer&apos;s details</p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Customer Name *</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Full name"
                    value={customerData.name}
                    onChange={function(e) { handleCustomerChange('name', e.target.value); }}
                    className="h-14 pl-12 rounded-sm"
                    data-testid="customer-name"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Company (Optional)</Label>
                <div className="relative">
                  <Building className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Company name"
                    value={customerData.company}
                    onChange={function(e) { handleCustomerChange('company', e.target.value); }}
                    className="h-14 pl-12 rounded-sm"
                    data-testid="customer-company"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Email (Optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={customerData.email}
                    onChange={function(e) { handleCustomerChange('email', e.target.value); }}
                    className="h-14 pl-12 rounded-sm"
                    data-testid="customer-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Phone (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="tel"
                    placeholder="+1 234 567 8900"
                    value={customerData.phone}
                    onChange={function(e) { handleCustomerChange('phone', e.target.value); }}
                    className="h-14 pl-12 rounded-sm"
                    data-testid="customer-phone"
                  />
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="text-gray-700 font-medium">Country *</Label>
                <Select value={customerData.country} onValueChange={function(v) { handleCustomerChange('country', v); }}>
                  <SelectTrigger className="h-14 rounded-sm" data-testid="customer-country">
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-gray-400" />
                      <SelectValue placeholder="Select country" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {countries.map(function(country) {
                      return (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <Button 
                type="button" 
                onClick={nextStep}
                className="bg-ink hover:bg-ink-800 text-white px-8 py-6 rounded-sm font-medium"
              >
                Continue <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Route Selection */}
        {step === 2 && (
          <div className="bg-white rounded-sm shadow-card p-8 animate-fadeInUp" data-testid="step-route">
            <h2 className="text-2xl font-normal tracking-[-0.01em] text-ink mb-6 flex items-center gap-3">
              <MapPin className="h-6 w-6 text-blue-600" />
              Select Route
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Origin port</Label>
                <PortSearch
                  ports={ports}
                  value={formData.origin_port}
                  onChange={function(v) { handleChange('origin_port', v); }}
                  placeholder="Search origin port..."
                  testId="origin-search"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Destination port</Label>
                <PortSearch
                  ports={ports}
                  value={formData.destination_port}
                  onChange={function(v) { handleChange('destination_port', v); }}
                  placeholder="Search destination port..."
                  testId="destination-search"
                />
              </div>
            </div>

            <div className="space-y-2 mt-6">
              <Label className="text-gray-700 font-medium">Cargo ready / pickup date <span className="text-red-500">*</span></Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-14 rounded-sm justify-start text-left font-normal"
                    data-testid="pickup-date"
                  >
                    <CalendarIcon className="mr-3 h-5 w-5 text-gray-400" />
                    {pickupDate ? format(pickupDate, "PPP") : "Select pickup date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={pickupDate}
                    onSelect={handleDateSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-gray-500">Past dates are allowed for shipments that already moved — the booking is recorded retrospectively and milestones can be dated when they actually happened.</p>
              {isBackdated && (
                <p className="text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="backdated-hint">Backdated booking — {format(pickupDate, 'PPP')} is in the past.</p>
              )}
            </div>

            <div className="flex justify-between mt-8">
              <Button 
                type="button" 
                onClick={prevStep}
                variant="outline"
                className="px-8 py-6 rounded-sm font-medium"
              >
                <ArrowLeft className="mr-2 h-5 w-5" /> Back
              </Button>
              <Button 
                type="button" 
                onClick={nextStep}
                className="bg-ink hover:bg-ink-800 text-white px-8 py-6 rounded-sm font-medium"
              >
                Continue <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Cargo Details */}
        {step === 3 && (
          <div className="bg-white rounded-sm shadow-card p-8 animate-fadeInUp" data-testid="step-cargo">
            <h2 className="text-2xl font-normal tracking-[-0.01em] text-ink mb-6 flex items-center gap-3">
              <Package className="h-6 w-6 text-blue-600" />
              Cargo Details
            </h2>

            <ShipmentModeToggle value={formData.shipment_mode} onChange={function(v) { handleChange('shipment_mode', v); }} />

            {formData.shipment_mode === 'bulk' ? (
              <BulkCargoFields formData={formData} onChange={handleChange} options={bulkOptions} />
            ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Cargo type</Label>
                <Select value={formData.cargo_type} onValueChange={function(v) { handleChange('cargo_type', v); }}>
                  <SelectTrigger className="h-14 rounded-sm" data-testid="cargo-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cargoTypes.map(function(type) {
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Container type</Label>
                <Select value={formData.container_type} onValueChange={function(v) { handleChange('container_type', v); }}>
                  <SelectTrigger className="h-14 rounded-sm" data-testid="container-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {containerTypes.map(function(type) {
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Number of Containers</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.container_count}
                  onChange={function(e) { handleChange('container_count', parseInt(e.target.value) || 1); }}
                  className="h-14 rounded-sm"
                  data-testid="container-count"
                />
              </div>

            </div>
            )}

            <div className="space-y-2 mt-6">
              <Label className="text-gray-700 font-medium">Products *</Label>
              <p className="text-sm text-gray-500">One line per product — cuts or grades with different HS codes are printed as separate lines on the bill of lading. Total weight is summed from the lines.</p>
              <CargoLinesEditor lines={formData.cargo_lines} onChange={function (v) { handleChange('cargo_lines', v); }} mode="booking" isBulk={formData.shipment_mode === 'bulk'} bulkCommodity={formData.shipment_mode === 'bulk' ? formData.bulk_commodity : undefined} />
            </div>

            <div className="space-y-2 mt-6">
              <Label className="text-gray-700 font-medium">Special Requirements (Optional)</Label>
              <Textarea
                placeholder="Any special handling instructions or requirements"
                value={formData.special_requirements}
                onChange={function(e) { handleChange('special_requirements', e.target.value); }}
                className="rounded-sm"
                data-testid="special-requirements"
              />
            </div>

            <div className="flex justify-between mt-8">
              <Button 
                type="button" 
                onClick={prevStep}
                variant="outline"
                className="px-8 py-6 rounded-sm font-medium"
              >
                <ArrowLeft className="mr-2 h-5 w-5" /> Back
              </Button>
              <Button 
                type="button" 
                onClick={nextStep}
                className="bg-ink hover:bg-ink-800 text-white px-8 py-6 rounded-sm font-medium"
              >
                Continue <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="bg-white rounded-sm shadow-card p-8 animate-fadeInUp" data-testid="step-confirm">
            <h2 className="text-2xl font-normal tracking-[-0.01em] text-ink mb-6 flex items-center gap-3">
              <Check className="h-6 w-6 text-blue-600" />
              Review & Confirm
            </h2>

            {/* Customer Summary */}
            <div className="bg-gradient-to-br from-[#1F97CC]/10 to-[#0F1B26]/10 rounded-sm p-6 mb-6">
              <h3 className="font-medium text-ink mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Walk-in Customer
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="ml-2 font-medium text-ink">{customerData.name}</span>
                </div>
                {customerData.company && (
                  <div>
                    <span className="text-gray-500">Company:</span>
                    <span className="ml-2 font-medium text-ink">{customerData.company}</span>
                  </div>
                )}
                {customerData.email && (
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2 font-medium text-ink">{customerData.email}</span>
                  </div>
                )}
                {customerData.phone && (
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <span className="ml-2 font-medium text-ink">{customerData.phone}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Country:</span>
                  <span className="ml-2 font-medium text-ink">{customerData.country}</span>
                </div>
              </div>
            </div>

            {/* Booking Summary */}
            <div className="space-y-6">
              {/* Route */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-sm">
                <div className="flex-1">
                  <p className="text-sm text-gray-500">From</p>
                  <p className="font-medium text-ink">{formData.origin_port}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <Ship className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 text-right">
                  <p className="text-sm text-gray-500">To</p>
                  <p className="font-medium text-ink">{formData.destination_port}</p>
                </div>
              </div>

              {/* Cargo Info */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {formData.shipment_mode === 'bulk' ? <BulkSummary formData={formData} /> : (
                <>
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500">Cargo type</p>
                  <p className="font-medium text-ink">{formData.cargo_type}</p>
                </div>
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500">Container</p>
                  <p className="font-medium text-ink">{formData.container_count}x {formData.container_type}</p>
                </div>
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500">Total gross weight</p>
                  <p className="font-medium text-ink" data-testid="review-weight">{linesTotals(formData.cargo_lines, false).gross.toLocaleString()} kg</p>
                </div>
                </>
                )}
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500">Pickup date</p>
                  <p className="font-medium text-ink">{pickupDate ? format(pickupDate, "PPP") : '-'}{isBackdated && <span className="ml-2 text-[11px] font-normal text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-full align-middle" data-testid="review-backdated-tag">backdated</span>}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-sm p-4" data-testid="review-products">
                <p className="text-sm text-gray-500">Products ({formData.cargo_lines.length})</p>
                <ul className="divide-y divide-gray-200 mt-1">
                  {formData.cargo_lines.map(function (l, i) {
                    const digits = (l.hs_code || '').replace(/\D/g, '');
                    return (
                      <li key={l._key} className="py-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-testid={'review-product-' + i}>
                        <span className="font-medium text-ink">{l.description}</span>
                        <span className="text-sm text-gray-600 sv-mono">{digits ? 'HS ' + formatHs(digits) : 'HS —'}{formData.shipment_mode !== 'bulk' && l.package_count ? ' · ' + Number(l.package_count).toLocaleString() + ' ' + (l.package_type || 'cartons').toLowerCase() : ''}{formData.shipment_mode !== 'bulk' && l.gross_weight_kg ? ' · ' + Number(l.gross_weight_kg).toLocaleString() + ' kg' : ''}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {formData.special_requirements && (
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500">Special requirements</p>
                  <p className="font-medium text-ink">{formData.special_requirements}</p>
                </div>
              )}

              {/* Price */}
              <div className="bg-ink rounded-sm p-6 text-white">
                <div className="flex items-center justify-between">
                  <span className="text-white/80">Estimated total</span>
                  <span className="text-[32px] sv-display">{formatCurrency(estimatedPrice)}</span>
                </div>
                <p className="text-white/60 text-sm mt-2">Final price may vary based on actual shipment details</p>
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <Button 
                type="button" 
                onClick={prevStep}
                variant="outline"
                className="px-8 py-6 rounded-sm font-medium"
              >
                <ArrowLeft className="mr-2 h-5 w-5" /> Back
              </Button>
              <Button 
                type="submit" 
                className="bg-ink hover:bg-ink-800 text-white px-10 py-6 rounded-sm font-medium"
                disabled={loading}
                data-testid="submit-booking"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating Booking...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Create Booking
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default AgentWalkinBooking;
